import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarIcon,
  CalendarDaysIcon,
  CurrencyPoundIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CogIcon,
  UsersIcon,
  DeviceTabletIcon,
  BuildingOfficeIcon,
  KeyIcon,
  SparklesIcon,
  PresentationChartLineIcon,
  MegaphoneIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  ArrowPathRoundedSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { menuConfig, iconMap } from '../../config/menuConfig';

const EnterpriseLeftSidebar = ({ isOpen, onClose }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [dynamicNavigation, setDynamicNavigation] = useState([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Load user permissions and navigation
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (user?.role) {
        try {
          const response = await fetch('/api/menu-permissions/user/permissions', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
          });

          if (response.ok) {
            const data = await response.json();

            const allowedMenuItems = menuConfig.filter(item => {
              const modulePermissions = data.permissions[item.module];
              return modulePermissions && modulePermissions.includes('view');
            }).map(item => ({
              ...item,
              icon: iconMap[item.icon],
              subItems: item.subItems ? item.subItems.filter(subItem => {
                const subModulePermissions = data.permissions[subItem.module];
                return subModulePermissions && subModulePermissions.includes('view');
              }) : undefined
            }));

            setDynamicNavigation(allowedMenuItems);
          } else {
            setDynamicNavigation(getStaticNavigation(user.role));
          }
        } catch (error) {
          console.error('Error loading user permissions:', error);
          setDynamicNavigation(getStaticNavigation(user.role));
        }
      }
    };

    loadUserPermissions();
  }, [user?.role]);

  const getStaticNavigation = (role) => {
    const staticNav = [
      { name: 'Home', href: '/', icon: HomeIcon, roles: ['employee', 'manager', 'admin', 'hr_manager', 'supervisor', 'accountant'] },
      { name: 'Pending Requests', href: '/dashboard', icon: ClockIcon, roles: ['employee', 'manager', 'admin', 'hr_manager', 'supervisor', 'accountant'] },
      { name: 'Announcements', href: '/announcements', icon: MegaphoneIcon, roles: ['admin', 'hr_manager'] },
      {
        name: 'Organization',
        href: '/organization',
        icon: BuildingOfficeIcon,
        roles: ['admin', 'hr_manager', 'manager'],
        subItems: [
          { name: 'Company Structure', href: '/organization/companies', roles: ['admin', 'hr_manager'] },
          { name: 'Departments', href: '/organization/departments', roles: ['admin', 'hr_manager', 'manager'] },
          { name: 'Divisions', href: '/organization/divisions', roles: ['admin', 'hr_manager', 'manager'] },
          { name: 'Hierarchy View', href: '/organization/hierarchy', roles: ['admin', 'hr_manager', 'manager'] },
          { name: 'Role Management', href: '/organization/roles', roles: ['admin', 'hr_manager'] }
        ]
      },
      { name: 'Employees', href: '/employees', icon: UserGroupIcon, roles: ['manager', 'admin', 'hr_manager'] },
      { name: 'User Management', href: '/user-management', icon: UsersIcon, roles: ['admin', 'hr_manager'] },
      { name: 'Attendance', href: '/attendance', icon: ClockIcon, roles: ['employee', 'manager', 'admin', 'hr_manager', 'supervisor'] },
      { name: 'Attendance Management', href: '/attendance-management', icon: ClockIcon, roles: ['manager', 'admin', 'hr_manager', 'supervisor'] },
      { name: 'Schedules', href: '/schedules', icon: CalendarDaysIcon, roles: ['manager', 'admin', 'hr_manager', 'supervisor', 'employee'] },
      { name: 'Enterprise Scheduling', href: '/enterprise-scheduling', icon: SparklesIcon, roles: ['manager', 'admin', 'hr_manager', 'supervisor'] },
      { name: 'UK Compliance', href: '/uk-compliance-dashboard', icon: ShieldCheckIcon, roles: ['admin', 'hr_manager', 'manager'] },
      { name: 'UK Scheduling', href: '/uk-scheduling', icon: CalendarDaysIcon, roles: ['admin', 'hr_manager', 'manager'] },
      { name: 'Time & Attendance', href: '/time-attendance', icon: ClipboardDocumentCheckIcon, roles: ['admin', 'hr_manager', 'manager'] },
      { name: 'Leave Requests', href: '/leaves', icon: CalendarIcon, roles: ['employee', 'manager', 'admin', 'hr_manager', 'supervisor'] },
      { name: 'Calendar Sync', href: '/calendar-sync', icon: ArrowPathRoundedSquareIcon, roles: ['employee', 'manager', 'admin', 'hr_manager', 'supervisor', 'accountant'] },
      { name: 'Payroll', href: '/payroll', icon: CurrencyPoundIcon, roles: ['manager', 'admin', 'hr_manager', 'accountant'] },
      { name: 'HMRC Compliance', href: '/hmrc', icon: DocumentTextIcon, roles: ['admin', 'accountant'] },
      { name: 'Documents', href: '/documents', icon: DocumentTextIcon, roles: ['admin', 'hr_manager', 'manager'] },
      { name: 'Reports', href: '/reports', icon: ChartBarIcon, roles: ['manager', 'admin', 'hr_manager', 'accountant', 'supervisor'] },
      { name: 'Enterprise Reporting', href: '/enterprise-reporting', icon: PresentationChartLineIcon, roles: ['manager', 'admin', 'hr_manager', 'accountant'] },
      { name: 'Probationary', href: '/probationary', icon: AcademicCapIcon, roles: ['admin', 'hr_manager', 'manager'] },
      { name: 'Approval Workflows', href: '/workflow-management', icon: ClipboardDocumentListIcon, roles: ['admin', 'hr_manager'] },
      { name: 'Pending Approvals', href: '/pending-approvals', icon: CheckCircleIcon, roles: ['admin', 'hr_manager', 'manager'] },
      { name: 'Kiosk Mode', href: '/kiosk', icon: DeviceTabletIcon, roles: ['admin', 'hr_manager', 'manager', 'supervisor'] },
      { name: 'Permission Settings', href: '/permission-settings', icon: KeyIcon, roles: ['admin', 'hr_manager'] },
      { name: 'Settings', href: '/settings', icon: CogIcon, roles: ['admin', 'hr_manager'] },
      { name: 'SSO Settings', href: '/sso-settings', icon: ShieldCheckIcon, roles: ['admin', 'hr_manager'] },
    ];

    return staticNav.filter(item => !item.roles || item.roles.includes(role));
  };

  const processedNavigation = dynamicNavigation.length > 0
    ? dynamicNavigation.map(item => ({
        ...item,
        icon: iconMap[item.icon] || HomeIcon
      }))
    : getStaticNavigation(user?.role);

  const isCurrentPath = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const toggleExpanded = (itemName) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemName)) {
      newExpanded.delete(itemName);
    } else {
      newExpanded.add(itemName);
    }
    setExpandedItems(newExpanded);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 flex z-40 lg:hidden"
          onClick={onClose}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 flex flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-r border-slate-700
        transform lg:translate-x-0 transition-all duration-300 ease-in-out shadow-2xl
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isSidebarCollapsed ? 'w-20' : 'w-72'}
      `}>

        {/* Logo & Brand */}
        <div className="flex items-center justify-between h-16 px-4 bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg">
          {!isSidebarCollapsed && (
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-md">
                <span className="text-xl font-bold text-blue-600">HR</span>
              </div>
              <div>
                <h1 className="text-base font-bold text-white">HR & Payroll</h1>
                <p className="text-xs text-blue-100">Enterprise System</p>
              </div>
            </div>
          )}
          {isSidebarCollapsed && (
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-md mx-auto">
              <span className="text-xl font-bold text-blue-600">HR</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1 custom-scrollbar">
          {processedNavigation.map((item) => {
            const current = isCurrentPath(item.href);
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isExpanded = expandedItems.has(item.name);
            const filteredSubItems = hasSubItems
              ? item.subItems.filter(subItem => !subItem.roles || subItem.roles.includes(user?.role))
              : [];

            return (
              <div key={item.name} className="space-y-0.5">
                {hasSubItems ? (
                  <button
                    onClick={() => toggleExpanded(item.name)}
                    className={`
                      group flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                      ${current || filteredSubItems.some(sub => isCurrentPath(sub.href))
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/50'
                        : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                      }
                    `}
                  >
                    {React.createElement(item.icon, {
                      className: `h-5 w-5 flex-shrink-0 ${
                        current || filteredSubItems.some(sub => isCurrentPath(sub.href))
                          ? 'text-blue-100'
                          : 'text-slate-400 group-hover:text-slate-200'
                      }`
                    })}
                    {!isSidebarCollapsed && (
                      <>
                        <span className="flex-1 ml-3 text-left">{item.name}</span>
                        {isExpanded ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={`
                      group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                      ${current
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/50'
                        : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                      }
                    `}
                    onClick={onClose}
                  >
                    {React.createElement(item.icon, {
                      className: `h-5 w-5 flex-shrink-0 ${
                        current ? 'text-blue-100' : 'text-slate-400 group-hover:text-slate-200'
                      }`
                    })}
                    {!isSidebarCollapsed && (
                      <span className="ml-3">{item.name}</span>
                    )}
                  </Link>
                )}

                {/* Sub-items */}
                {hasSubItems && isExpanded && !isSidebarCollapsed && (
                  <div className="ml-8 mt-1 space-y-0.5 border-l-2 border-slate-700 pl-3">
                    {filteredSubItems.map((subItem) => {
                      const subCurrent = isCurrentPath(subItem.href);
                      return (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className={`
                            group flex items-center px-3 py-2 text-xs font-medium rounded-md transition-all duration-200
                            ${subCurrent
                              ? 'bg-blue-900/30 text-blue-300 border-l-2 border-blue-400'
                              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                            }
                          `}
                          onClick={onClose}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0 ${
                            subCurrent ? 'bg-blue-400' : 'bg-slate-500 group-hover:bg-slate-400'
                          }`}></span>
                          {subItem.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* User info & Actions */}
        <div className="flex-shrink-0 border-t border-slate-700 bg-slate-900/50">
          {!isSidebarCollapsed ? (
            <div className="p-4">
              {/* User Profile */}
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-sm font-semibold text-white">
                    {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.first_name && user?.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user?.email
                    }
                  </p>
                  <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                <button
                  className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                  title="Notifications"
                >
                  <BellIcon className="h-4 w-4" />
                </button>
                <button
                  className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                  title="Profile"
                >
                  <UserCircleIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-2">
              <button
                className="w-full flex items-center justify-center p-2 text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                title="Notifications"
              >
                <BellIcon className="h-5 w-5" />
              </button>
              <button
                className="w-full flex items-center justify-center p-2 text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                title="Profile"
              >
                <UserCircleIcon className="h-5 w-5" />
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center p-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(51, 65, 85, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.7);
        }
      `}</style>
    </>
  );
};

export default EnterpriseLeftSidebar;
